import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DataDeletionRequest = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please provide your name and email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/data-deletion-request`, form);
      setConfirmation(res.data);
      toast.success('Your data deletion request has been submitted.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="legal-page" data-testid="data-deletion-page">
      <section className="legal-hero">
        <div className="container">
          <h1 className="legal-title">Data Deletion Request</h1>
          <p className="legal-effective">Effective Date: August 1, 2026</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          <div className="legal-text">
            <p>
              Giant-Tree Foundation (operating as Elderra) respects your right to control your personal data.
              If you would like us to delete the personal information we hold about you — including any data
              collected via our website, WhatsApp services, or connected third-party platforms such as
              Facebook/Meta — you can submit a request below, or email us directly.
            </p>

            <h2>1. How to Request Data Deletion</h2>
            <p>You can request deletion of your data in either of the following ways:</p>
            <ul>
              <li>Fill out the form below with your details and reason for deletion.</li>
              <li>
                Email us directly at{' '}
                <a href="mailto:support@elderra.io">support@elderra.io</a> with the subject line "Data Deletion
                Request", including your registered name, email, and phone number.
              </li>
            </ul>

            <h2>2. What Happens Next</h2>
            <p>
              Once we receive your request, we will verify your identity and initiate deletion of your personal
              data from our records within <strong>30 days</strong>. You will receive a unique confirmation code
              that you can use to check the status of your request at any time. Some data may be retained where
              required by law (e.g. financial/tax records for donations, as mandated under the Income Tax Act).
            </p>

            <h2>3. Facebook / Meta Platform Users</h2>
            <p>
              If you interacted with Elderra through a Facebook or Meta-connected feature (such as our WhatsApp
              Business integration), you may also request deletion of your data via this page in accordance with
              Meta's Platform Terms. We do not sell or share your data with any third party without consent.
            </p>
          </div>

          <div className="legal-text" style={{ marginTop: '2.5rem' }}>
            {confirmation ? (
              <div
                className="deletion-confirmation-box"
                data-testid="deletion-confirmation-box"
              >
                <h2>Request Received</h2>
                <p>{confirmation.message}</p>
                <p>
                  Your confirmation code: <strong data-testid="confirmation-code">{confirmation.confirmation_code}</strong>
                </p>
                <p>Please save this code to check your request status. Contact support@elderra.io with this code for any queries.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="deletion-request-form" data-testid="data-deletion-form">
                <h2>Submit a Deletion Request</h2>
                <div className="form-group">
                  <label htmlFor="dd-name">Full Name *</label>
                  <input
                    id="dd-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="deletion-name-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dd-email">Email Address *</label>
                  <input
                    id="dd-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="deletion-email-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dd-phone">Phone Number</label>
                  <input
                    id="dd-phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="deletion-phone-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dd-reason">Reason (optional)</label>
                  <textarea
                    id="dd-reason"
                    name="reason"
                    rows={4}
                    value={form.reason}
                    onChange={handleChange}
                    data-testid="deletion-reason-input"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                  data-testid="deletion-submit-btn"
                >
                  {submitting ? <Loader2 className="spin-icon" size={18} /> : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataDeletionRequest;
